import {
  activityComponent,
  createActivityPolymiddleware,
  type ActivityPolymiddleware
} from '@msinternal/botframework-webchat-api-middleware';
import {
  type LegacyActivityMiddleware,
  type LegacyRenderAttachment
} from '@msinternal/botframework-webchat-api-middleware/legacy';
import { type WebChatActivity } from 'botframework-webchat-core';
import { composeEnhancer } from 'handler-chain';
import { type ReactNode } from 'react';
import {
  boolean,
  custom,
  function_,
  is,
  literal,
  never,
  object,
  optional,
  pipe,
  readonly,
  union,
  type InferInput
} from 'valibot';

import LegacyActivityBridge from './LegacyActivityBridge';

const DEBUG_ORIGINAL_LEGACY_MIDDLEWARE_SYMBOL = Symbol('OriginalLegacyMiddleware');
const webChatActivitySchema = custom<WebChatActivity>(value => is(object({}), value));

type LegacyRenderFunction = (
  renderAttachment: LegacyRenderAttachment,
  options: {
    readonly hideTimestamp: boolean;
    readonly renderActivityStatus: (options: { hideTimestamp: boolean }) => ReactNode;
    readonly renderAvatar: false | (() => Exclude<ReactNode, boolean | null | undefined>);
    readonly showCallout: boolean;
  }
) => Exclude<ReactNode, boolean>;

const legacyActivityBridgeComponentPropsSchema = pipe(
  object({
    activity: webChatActivitySchema,
    children: optional(never()),
    render: custom<LegacyRenderFunction>(value => is(function_(), value)),

    // The following extraneous props should be removed once `useCreateActivityRenderer()` is removed.
    hideTimestamp: optional(boolean()),
    renderActivityStatus: optional(
      custom<(options: { hideTimestamp: boolean }) => ReactNode>(value => is(function_(), value))
    ),
    renderAvatar: optional(
      union([
        literal(false),
        custom<() => Exclude<ReactNode, boolean | null | undefined>>(value => is(function_(), value))
      ])
    ),
    showCallout: optional(boolean())
  }),
  readonly()
);

type LegacyActivityBridgeComponentProps = Readonly<
  InferInput<typeof legacyActivityBridgeComponentPropsSchema> & { children?: never }
>;

const fallbackComponentPropsSchema = pipe(
  object({
    activity: webChatActivitySchema,
    children: optional(never())
  }),
  readonly()
);

type FallbackComponentProps = Readonly<InferInput<typeof fallbackComponentPropsSchema> & { children?: never }>;

/**
 * Polyfill legacy activity middleware into a polymiddleware.
 *
 * @deprecated Legacy activity middleware is being deprecated and will be removed on or after 2027-08-16.
 * @param middleware An array of legacy activity middleware.
 * @returns A polymiddleware composed by legacy activity middleware.
 */
function createActivityPolymiddlewareFromLegacy(
  ...middleware: readonly LegacyActivityMiddleware[]
): ActivityPolymiddleware {
  const legacyEnhancer = composeEnhancer(...middleware.map(middleware => middleware()));

  const polymiddleware = createActivityPolymiddleware(next => {
    const legacyHandler = legacyEnhancer(request => {
      const handler = next(request);

      return !!handler && (() => handler.render({}));
    });

    return request => {
      const legacyResult = legacyHandler(request);

      return legacyResult
        ? activityComponent(LegacyActivityBridge, { activity: request.activity, render: legacyResult })
        : undefined;
    };
  });

  Object.defineProperty(polymiddleware, DEBUG_ORIGINAL_LEGACY_MIDDLEWARE_SYMBOL, {
    configurable: false,
    value: Object.freeze([...middleware]),
    writable: false
  });

  return polymiddleware;
}

export default createActivityPolymiddlewareFromLegacy;

export {
  fallbackComponentPropsSchema,
  legacyActivityBridgeComponentPropsSchema,
  type FallbackComponentProps,
  type LegacyActivityBridgeComponentProps
};
